import { useEffect, useState } from "react";
import { api, formatApiError, ROLE_LABEL, ROLE_COLOR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function UsersPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [offices, setOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", full_name: "", role: "CONSULTANT", phone: "", office_id: "" });

  const isSuper = user?.role === "SUPER_ADMIN";

  const load = async () => {
    const { data } = await api.get("/users");
    setItems(data);
    if (isSuper) {
      const { data: o } = await api.get("/offices");
      setOffices(o);
    }
  };
  useEffect(() => { load(); }, [user?.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!isSuper) delete payload.office_id;
      await api.post("/users", payload);
      toast.success("Kullanıcı eklendi");
      setOpen(false);
      setForm({ username: "", password: "", full_name: "", role: "CONSULTANT", phone: "", office_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const roleOptions = ["BROKER", "ACCOUNTANT", "CONSULTANT"];

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Yönetim</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Kullanıcılar</h1>
          <p className="text-slate-500 mt-1">Ekip üyelerini ve erişim yetkilerini yönetin.</p>
        </div>
        <Button data-testid="add-user-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-2" />Yeni Kullanıcı</Button>
      </div>

      <Card className="divide-y divide-slate-100">
        {items.map((u) => (
          <div key={u.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold">{u.full_name?.[0]}</div>
              <div>
                <div className="font-semibold text-slate-900">{u.full_name}</div>
                <div className="flex items-center gap-2 text-xs mt-0.5">
                  <span className="text-slate-500">@{u.username}</span>
                  <Badge variant="outline" className={ROLE_COLOR[u.role]}>{ROLE_LABEL[u.role]}</Badge>
                </div>
              </div>
            </div>
            {u.id !== user.id && (
              <Button size="sm" variant="ghost" onClick={() => handleDelete(u.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            )}
          </div>
        ))}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Kullanıcı</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><Label>Ad Soyad *</Label><Input data-testid="user-fullname" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kullanıcı Adı *</Label><Input data-testid="user-username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
              <div><Label>Şifre *</Label><Input data-testid="user-password" type="text" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            {isSuper && form.role !== "SUPER_ADMIN" && (
              <div>
                <Label>Ofis</Label>
                <Select value={form.office_id} onValueChange={(v) => setForm({ ...form, office_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Ofis seçin" /></SelectTrigger>
                  <SelectContent>
                    {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.office_code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button data-testid="user-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
